import { lazy, Suspense, type ReactNode } from "react";
import { Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthGate, PublicOnly } from "./AuthGate";
import { RouteErrorBoundary } from "./RouteErrorBoundary";
import { AppShell } from "@/components/layout/AppShell";
import { ADMIN_ROLES, ORG_ADMIN_ROLES, type AppRole } from "@/types/roles";

// Route components are lazy-loaded so the app bundle splits per route instead
// of shipping every page (all 17 admin screens included) to every visitor.
// The admin console + its shell/provider are grouped into a single "admin"
// chunk (see vite.config.ts manualChunks) that field staff never download —
// it's also excluded from the PWA precache.
//
// Public + staff pages
const Landing = lazy(() => import("@/pages/Landing"));
const Apply = lazy(() => import("@/pages/Apply"));
const SignIn = lazy(() => import("@/pages/SignIn"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const SetPassword = lazy(() => import("@/pages/SetPassword"));
const Home = lazy(() => import("@/pages/Home"));
const Schedule = lazy(() => import("@/pages/Schedule"));
const Hours = lazy(() => import("@/pages/Hours"));
const Settings = lazy(() => import("@/pages/Settings"));
const StaffActivationDetail = lazy(() => import("@/pages/StaffActivationDetail"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Client portal — its own chunk, isolated from the staff + admin bundles so a
// client login never downloads a byte of internal app code.
const ClientShell = lazy(() =>
  import("@/components/layout/ClientShell").then((m) => ({ default: m.ClientShell })),
);
const ClientHome = lazy(() => import("@/pages/client/ClientHome"));
const ClientActivationDetail = lazy(() => import("@/pages/client/ClientActivationDetail"));

// Admin shell + org context — named exports, lazied so they land in the admin
// chunk rather than the main bundle.
const AdminShell = lazy(() =>
  import("@/components/layout/AdminShell").then((m) => ({ default: m.AdminShell })),
);
const AdminOrgProvider = lazy(() =>
  import("@/features/admin/AdminOrgContext").then((m) => ({ default: m.AdminOrgProvider })),
);
const AdminWorkspaceProvider = lazy(() =>
  import("@/features/admin/AdminWorkspaceContext").then((m) => ({
    default: m.AdminWorkspaceProvider,
  })),
);

// Admin pages
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProfile = lazy(() => import("@/pages/admin/AdminProfile"));
const ApplicationsList = lazy(() => import("@/pages/admin/ApplicationsList"));
const OrganizationsList = lazy(() => import("@/pages/admin/OrganizationsList"));
const OrganizationDetail = lazy(() => import("@/pages/admin/OrganizationDetail"));
const StaffList = lazy(() => import("@/pages/admin/StaffList"));
const StaffDetail = lazy(() => import("@/pages/admin/StaffDetail"));
const CrewList = lazy(() => import("@/pages/admin/CrewList"));
const ClientsList = lazy(() => import("@/pages/admin/ClientsList"));
const ActivationsList = lazy(() => import("@/pages/admin/ActivationsList"));
const ActivationDetail = lazy(() => import("@/pages/admin/ActivationDetail"));
const TimeLogsList = lazy(() => import("@/pages/admin/TimeLogsList"));
const InvoicesPage = lazy(() => import("@/pages/admin/InvoicesPage"));
const GeofencePage = lazy(() => import("@/pages/admin/GeofencePage"));
const AnalyticsPage = lazy(() => import("@/pages/admin/AnalyticsPage"));
const InquiriesList = lazy(() => import("@/pages/admin/InquiriesList"));
const TaskTemplatesList = lazy(() => import("@/pages/admin/TaskTemplatesList"));
const TaskTemplateDetail = lazy(() => import("@/pages/admin/TaskTemplateDetail"));
const MetricTemplatesList = lazy(() => import("@/pages/admin/MetricTemplatesList"));
const MetricTemplateDetail = lazy(() => import("@/pages/admin/MetricTemplateDetail"));
const WorkspacesList = lazy(() => import("@/pages/admin/WorkspacesList"));
const WorkspaceDetail = lazy(() => import("@/pages/admin/WorkspaceDetail"));
const OrgAdminsList = lazy(() => import("@/pages/admin/OrgAdminsPage"));

const SUPER_ADMIN_ROLES: AppRole[] = ["super_admin"];

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

type AdminRouteProps = {
  children: ReactNode;
  /** super_admin only. */
  superOnly?: boolean;
  /** org_admin or super_admin (org-wide management: workspaces, org admins). */
  orgAdminOnly?: boolean;
};

function AdminRoute({ children, superOnly, orgAdminOnly }: AdminRouteProps) {
  const requireRoles = superOnly
    ? SUPER_ADMIN_ROLES
    : orgAdminOnly
      ? ORG_ADMIN_ROLES
      : ADMIN_ROLES;
  return (
    <AuthGate requireRoles={requireRoles}>
      <AdminOrgProvider>
        <AdminWorkspaceProvider>
          <AdminShell>{children}</AdminShell>
        </AdminWorkspaceProvider>
      </AdminOrgProvider>
    </AuthGate>
  );
}

// Client-portal routes: gated to the "client" role. Non-clients are bounced to
// their own home; a client is also kept inside /client by AuthGate itself.
function ClientRoute({ children }: { children: ReactNode }) {
  return (
    <AuthGate requireRoles={["client"]}>
      <ClientShell>{children}</ClientShell>
    </AuthGate>
  );
}

export function AppRoutes() {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/apply" element={<Apply />} />

        <Route path="/sign-in" element={<PublicOnly><SignIn /></PublicOnly>} />
        <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
        {/* Landing page for email invites + password resets. Must not be PublicOnly —
            Supabase establishes a session from the URL fragment before redirecting. */}
        <Route path="/set-password" element={<SetPassword />} />

        <Route path="/app" element={<AuthGate><AppShell><Home /></AppShell></AuthGate>} />
        <Route path="/app/schedule" element={<AuthGate><AppShell><Schedule /></AppShell></AuthGate>} />
        <Route path="/app/hours" element={<AuthGate><AppShell><Hours /></AppShell></AuthGate>} />
        <Route path="/app/settings" element={<AuthGate><AppShell><Settings /></AppShell></AuthGate>} />
        <Route
          path="/app/activations/:activationId"
          element={
            <AuthGate>
              <AppShell>
                <StaffActivationDetail />
              </AppShell>
            </AuthGate>
          }
        />

        <Route path="/client" element={<ClientRoute><ClientHome /></ClientRoute>} />
        <Route
          path="/client/activations/:activationId"
          element={<ClientRoute><ClientActivationDetail /></ClientRoute>}
        />

        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/profile" element={<AdminRoute><AdminProfile /></AdminRoute>} />
        <Route
          path="/admin/organizations"
          element={
            <AdminRoute superOnly>
              <OrganizationsList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/organizations/:orgId"
          element={
            <AdminRoute superOnly>
              <OrganizationDetail />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/workspaces"
          element={
            <AdminRoute orgAdminOnly>
              <WorkspacesList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/workspaces/:id"
          element={
            <AdminRoute orgAdminOnly>
              <WorkspaceDetail />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/org-admins"
          element={
            <AdminRoute orgAdminOnly>
              <OrgAdminsList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/activations"
          element={
            <AdminRoute>
              <ActivationsList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/activations/:activationId"
          element={
            <AdminRoute>
              <ActivationDetail />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/time-logs"
          element={
            <AdminRoute>
              <TimeLogsList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/tasks"
          element={
            <AdminRoute>
              <TaskTemplatesList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/tasks/:templateId"
          element={
            <AdminRoute>
              <TaskTemplateDetail />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/metrics"
          element={
            <AdminRoute>
              <MetricTemplatesList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/metrics/:templateId"
          element={
            <AdminRoute>
              <MetricTemplateDetail />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/staff"
          element={
            <AdminRoute>
              <StaffList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/staff/:staffId"
          element={
            <AdminRoute>
              <StaffDetail />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/crew"
          element={
            <AdminRoute superOnly>
              <CrewList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/clients"
          element={
            <AdminRoute>
              <ClientsList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/geofence"
          element={
            <AdminRoute>
              <GeofencePage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/invoices"
          element={
            <AdminRoute orgAdminOnly>
              <InvoicesPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <AdminRoute superOnly>
              <AnalyticsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/applications"
          element={
            <AdminRoute superOnly>
              <ApplicationsList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/inquiries"
          element={
            <AdminRoute superOnly>
              <InquiriesList />
            </AdminRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
}
